'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Icons } from '@/components/ui/icons'
import {
  registerLockedCorrectionPrompt,
  type SampleLockedDetail,
} from '@/lib/locked-correction'

// Readable names for the locked fields the backend reports.
const FIELD_LABELS: Record<string, string> = {
  claim_type: 'claim Part',
  is_voided: 'claim void flag',
  sample_id: 'sample membership',
  added: 'records added',
  deleted: 'records deleted',
  status: 'reopening a completed claim',
  disagreement_type: 'disagreement type',
  da_type: 'disagreement finding',
  claim_id: 'disagreement claim',
  decision: 'review decision',
  determination: 'review determination',
  sample_name: 'sample name',
  sample_type_category: 'program',
  mrc_type: 'MRC type',
  mrac_sample_type: 'sample type',
  mac_jurisdiction: 'jurisdiction',
  mrc_workload_month: 'workload month',
  sample_submission_date: 'submission date',
  is_void: 'sample void flag',
}

function formatDay(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(`${iso}T12:00:00Z`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { timeZone: 'UTC' })
}

// The warning shown when a change would alter a sample already submitted to
// CMS (D46). An MR Manager or Program Manager can go ahead with a reason,
// which the backend writes to the audit log.
export function LockedCorrectionDialog() {
  const [detail, setDetail] = useState<SampleLockedDetail | null>(null)
  const [reason, setReason] = useState('')
  const resolver = useRef<((value: string | null) => void) | null>(null)

  useEffect(() => {
    registerLockedCorrectionPrompt(
      next =>
        new Promise<string | null>(resolve => {
          resolver.current?.(null)
          resolver.current = resolve
          setReason('')
          setDetail(next)
        }),
    )
    return () => {
      registerLockedCorrectionPrompt(null)
      resolver.current?.(null)
    }
  }, [])

  const finish = (value: string | null) => {
    resolver.current?.(value)
    resolver.current = null
    setDetail(null)
  }

  const submitted = formatDay(detail?.submitted_on)
  const changes = (detail?.changes ?? []).map(c => FIELD_LABELS[c] ?? c.replace(/_/g, ' '))

  return (
    <Dialog open={detail !== null} onOpenChange={open => { if (!open) finish(null) }}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Icons.alertTriangle className='h-5 w-5 text-amber-500' aria-hidden='true' />
            Correct submitted sample data?
          </DialogTitle>
          <DialogDescription>
            <span className='font-semibold text-foreground'>{detail?.sample_name}</span> was submitted
            to CMS{submitted ? ` on ${submitted}` : ''}. Its data is part of the monthly Invoice
            Detail and is locked.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-3 text-sm'>
          {changes.length > 0 && (
            <p className='text-muted-foreground'>
              This change affects: <span className='text-foreground'>{changes.join(', ')}</span>.
            </p>
          )}
          <p className='text-muted-foreground'>
            The correction will be recorded in the audit log with your name and the reason below.
          </p>
          <div>
            <label htmlFor='locked-correction-reason' className='mb-1 block text-xs font-semibold uppercase text-muted-foreground'>
              Reason for the correction
            </label>
            <textarea
              id='locked-correction-reason'
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              className='w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-cornflower'
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => finish(null)}>Cancel</Button>
          <Button onClick={() => finish(reason.trim())} disabled={!reason.trim()}>
            Make correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
